import Phaser from 'phaser';
import {
  ShootingMode,
  strokePower,
  strokeVelocity,
  strokeAim,
  simulateStroke,
  buildCollisionMap,
  applyMutationsToTrack,
  type StrokeResult,
  type SimBall,
  type Vec2,
  type CollisionMap,
  type MapMutation,
  type Track,
  MAX_POWER,
  POWER_DEAD_ZONE,
  POWER_DIVISOR,
  BALL_RADIUS,
  AIM_LINE_MAX_PX,
  FRAME_MS,
} from '@jgolf/shared';
import { CONFIG, RENDER_SCALE, toScreen } from '../config.js';
import { net, Rest, type RoomStateView, type RoomPlayer } from '../net.js';
import { paintField, trackFromMessage } from '../render.js';
import { Sound, unlockAudio } from '../audio.js';
import { Music } from '../music.js';
import { hud } from '../ui/hud.js';

interface HoleData {
  trackId: string;
  name: string;
  par: number;
  holeIndex: number;
  holeCount: number;
  tee: Vec2;
  cup: Vec2;
  tiles: number[];
}

interface BallGfx {
  circle: Phaser.GameObjects.Arc;
  shadow: Phaser.GameObjects.Arc;
  colorIndex: number;
  /** Render-only offset (screen px) so idle balls don't stack on the tee. */
  ox: number;
  oy: number;
}

const MODE_NAMES = ['Direct', 'Reverse', 'Right', 'Left'];

/** Identical to the server formula so prediction matches authoritative sim. */
function strokeSeed(holeIndex: number, strokesBefore: number, sessionId: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < sessionId.length; i++) {
    h ^= sessionId.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((holeIndex + 1) * 1000003 + (strokesBefore + 1) * 7919 + (h >>> 0)) >>> 0;
}

export class GameScene extends Phaser.Scene {
  private fieldImage?: Phaser.GameObjects.Image;
  private flag?: Phaser.GameObjects.Container;
  private aim!: Phaser.GameObjects.Graphics;
  private balls = new Map<string, BallGfx>();
  private labels = new Map<string, Phaser.GameObjects.Text>();

  private hole?: HoleData;
  private track?: Track;
  private fieldKey = '';
  private collisionMap?: CollisionMap;
  private cup: Vec2 = { x: 0, y: 0 };
  /** Course changes from the running replay, applied at their replay time. */
  private pendingMutations: Array<{ at: number; m: MapMutation }> = [];
  private mutatedThisReplay = false;

  private stateView?: RoomStateView;
  private mode: ShootingMode = ShootingMode.Normal;

  /** Touch drag state (touch aims by dragging; mouse aims by hovering). */
  private aiming = false;
  private aimStart = new Phaser.Math.Vector2();
  private aimCurr = new Phaser.Math.Vector2();
  private dragStart = new Phaser.Math.Vector2();
  private activePointerId: number | null = null;
  /** Latest mouse position (game coords) for authentic hover-aim. */
  private mousePos: Phaser.Math.Vector2 | null = null;

  private replay: { result: StrokeResult; start: number; idx: number; soundsDone: Set<number> } | null = null;
  private predicting = false;
  private pendingHoleComplete: unknown = null;
  private introShown = false;
  private gameOverShown = false;
  private unbinders: Array<() => void> = [];
  private presenceTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    super('GameScene');
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#0c1b12');
    this.aim = this.add.graphics();
    this.aim.setDepth(100);

    hud.mount(this);

    this.bindNet();
    this.setupInput();
    this.setupPresence();

    // hole_data may have arrived during join, before this scene existed.
    if (net.lastHole) this.onHoleData(net.lastHole as HoleData);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private bindNet(): void {
    this.unbinders.push(net.on('hole_data', (p) => this.onHoleData(p as HoleData)));
    this.unbinders.push(net.on('state', (p) => this.onState(p as RoomStateView)));
    this.unbinders.push(net.on('stroke_result', (p) => this.onStrokeResult(p as { by: string; result: StrokeResult })));
    this.unbinders.push(net.on('turn', (p) => this.onTurn(p as { currentTurn: string; deadline: number })));
    this.unbinders.push(net.on('hole_complete', (p) => this.onHoleComplete(p)));
    this.unbinders.push(net.on('game_over', (p) => this.onGameOver(p)));
    this.unbinders.push(net.on('emote', (p) => this.onEmote(p as { sessionId: string; emote: string })));
    this.unbinders.push(net.on('turn_skipped', (p) => hud.toast(`${(p as { name: string }).name} ran out of time`)));
    this.unbinders.push(net.on('spectating', () => hud.toast('Spectating — watching live')));
    // Opponent presence: the seat is held for 5 minutes; show what's happening.
    this.unbinders.push(net.on('player_disconnected', (p) => hud.toast(`${(p as { name: string }).name} disconnected — holding their seat`)));
    this.unbinders.push(net.on('player_reconnected', (p) => hud.toast(`${(p as { name: string }).name} reconnected`)));
    // Own connection: transient drops show a banner; recovery resyncs state.
    this.unbinders.push(net.on('reconnecting', () => hud.setConnection('reconnecting')));
    this.unbinders.push(
      net.on('reconnected', () => {
        hud.setConnection('ok');
        hud.toast('Reconnected');
      }),
    );
    this.unbinders.push(net.on('reconnect_failed', () => hud.showConnectionLost()));
    // Consented close we didn't ask for (e.g. server restarted): no seat is
    // held, so reconnecting is pointless — offer the way back to the lobby.
    this.unbinders.push(
      net.on('left', (p) => {
        const { code } = p as { code: number };
        if (code === 1000 && !net.leftIntentionally && !net.isSpectator && !this.gameOverShown) hud.showConnectionLost();
      }),
    );
  }

  /** Keep this player visible in the lobby list (as "in game") and fast-path
   * room reconnects when the tab returns from the background. */
  private setupPresence(): void {
    void Rest.heartbeat('in-game').catch(() => {});
    this.presenceTimer = setInterval(() => void Rest.heartbeat('in-game').catch(() => {}), 30_000);
    const onVisibility = () => {
      if (!document.hidden) net.onVisible();
    };
    const onPageHide = () => Rest.leaveBeacon();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    this.unbinders.push(() => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    });
  }

  private teardown(): void {
    this.unbinders.forEach((u) => u());
    this.unbinders = [];
    if (this.presenceTimer) clearInterval(this.presenceTimer);
    this.presenceTimer = null;
    hud.unmount();
  }

  // ── Hole setup ──────────────────────────────────────────────
  private onHoleData(data: HoleData): void {
    this.hole = data;
    this.cup = data.cup;
    this.pendingHoleComplete = null;
    this.pendingMutations = [];
    this.mutatedThisReplay = false;
    this.gameOverShown = false;
    hud.hideOverlay();
    const track = trackFromMessage(data.tiles, data.tee, data.cup);
    this.track = track;
    this.collisionMap = buildCollisionMap(track);

    const key = `field-${data.holeIndex}-${Date.now()}`;
    this.fieldKey = key;
    const canvas = paintField(track);
    if (this.textures.exists(key)) this.textures.remove(key);
    this.textures.addCanvas(key, canvas);
    this.fieldImage?.destroy();
    this.fieldImage = this.add.image(0, 0, key).setOrigin(0, 0).setScale(RENDER_SCALE).setDepth(0);

    this.drawFlag(data.cup);
    hud.setHoleInfo(data.holeIndex + 1, data.holeCount, data.name, data.par);
    Music.playHole(data.holeIndex, data.holeCount);

    // A brief course-intro flourish as the match starts (first hole only).
    if (!this.introShown) {
      this.introShown = true;
      hud.showCourseIntro(data.holeIndex + 1, data.holeCount, data.name, data.par);
    }
  }

  private drawFlag(cup: Vec2): void {
    this.flag?.destroy();
    const x = toScreen(cup.x);
    const y = toScreen(cup.y);
    const cont = this.add.container(x, y).setDepth(5);
    const hole = this.add.circle(0, 0, BALL_RADIUS * RENDER_SCALE * 1.15, 0x05060a);
    const pole = this.add.rectangle(0, -22, 2, 24, 0xdddddd).setOrigin(0.5, 1);
    const flagTri = this.add.triangle(1, -44, 0, 0, 16, 5, 0, 10, 0xff5a5a).setOrigin(0, 0);
    cont.add([hole, pole, flagTri]);
    this.flag = cont;
  }

  // ── State sync ──────────────────────────────────────────────
  private onState(view: RoomStateView): void {
    this.stateView = view;
    for (const p of view.players) this.ensureBall(p);
    // remove gone balls
    for (const id of Array.from(this.balls.keys())) {
      if (!view.players.find((p) => p.sessionId === id)) {
        this.balls.get(id)?.circle.destroy();
        this.balls.get(id)?.shadow.destroy();
        this.labels.get(id)?.destroy();
        this.balls.delete(id);
        this.labels.delete(id);
      }
    }
    this.updateTeeOffsets(view);
    // snap ball positions when not animating
    if (!this.replay && !this.predicting) {
      for (const p of view.players) this.setBallPos(p.sessionId, p.ballX, p.ballY, p.holed);
    }
    hud.updateScoreboard(view, net.sessionId);
    hud.updateTurn(view, net.sessionId);
    hud.updateChat(view, net.sessionId);

    // Rejoined (e.g. page reload) after the round already ended: rebuild the
    // results card from state since the game_over message is long gone.
    if (view.phase === 'round_over' && !this.gameOverShown && !this.replay) {
      this.gameOverShown = true;
      const sorted = [...view.players].sort((a, b) => a.strokesTotal - b.strokesTotal);
      hud.showGameOver(
        {
          winnerId: sorted[0]?.sessionId,
          players: view.players.map((p) => ({ sessionId: p.sessionId, name: p.name, strokesTotal: p.strokesTotal, holeScores: p.holeScores })),
        },
        net.sessionId,
      );
    }
  }

  /**
   * Fan idle, unplayed balls off the shared tee so they never visually stack
   * (which read as a phantom "collision" on the first stroke). Render-only:
   * authoritative physics positions are unchanged. The player whose turn it is
   * stays exactly on the tee so their aim line starts from the real ball.
   */
  private updateTeeOffsets(view: RoomStateView): void {
    const stacked = view.players
      .filter((p) => p.strokesThisHole === 0 && !p.holed && p.sessionId !== view.currentTurn)
      .sort((a, b) => a.colorIndex - b.colorIndex);
    const offsets = new Map<string, { ox: number; oy: number }>();
    if (stacked.length > 0) {
      const r = BALL_RADIUS * RENDER_SCALE * 1.5;
      const k = stacked.length;
      stacked.forEach((p, i) => {
        const ang = (i / k) * Math.PI * 2 - Math.PI / 2;
        offsets.set(p.sessionId, { ox: Math.cos(ang) * r, oy: Math.sin(ang) * r });
      });
    }
    for (const [id, b] of this.balls) {
      const o = offsets.get(id) ?? { ox: 0, oy: 0 };
      b.ox = o.ox;
      b.oy = o.oy;
    }
  }

  private ensureBall(p: RoomPlayer): void {
    if (this.balls.has(p.sessionId)) return;
    const color = CONFIG.BALL_COLORS[p.colorIndex % CONFIG.BALL_COLORS.length];
    const r = BALL_RADIUS * RENDER_SCALE;
    const shadow = this.add.circle(0, 0, r, 0x000000, 0.25).setDepth(9);
    const circle = this.add.circle(0, 0, r, color).setStrokeStyle(2, 0x222222, 0.6).setDepth(10);
    this.balls.set(p.sessionId, { circle, shadow, colorIndex: p.colorIndex, ox: 0, oy: 0 });
    const label = this.add.text(0, 0, p.name, { fontSize: '12px', color: '#fff', fontFamily: 'system-ui' }).setOrigin(0.5, 1.6).setDepth(11);
    this.labels.set(p.sessionId, label);
    this.setBallPos(p.sessionId, p.ballX, p.ballY, p.holed);
  }

  private setBallPos(id: string, nx: number, ny: number, holed: boolean): void {
    const b = this.balls.get(id);
    if (!b) return;
    const x = toScreen(nx) + b.ox;
    const y = toScreen(ny) + b.oy;
    b.circle.setPosition(x, y);
    b.shadow.setPosition(x + 2, y + 2);
    b.circle.setVisible(!holed);
    b.shadow.setVisible(!holed);
    const label = this.labels.get(id);
    label?.setPosition(x, y - BALL_RADIUS * RENDER_SCALE);
    label?.setVisible(!holed);
  }

  // ── Turn / input ────────────────────────────────────────────
  private onTurn(_p: { currentTurn: string; deadline: number }): void {
    if (this.stateView) hud.updateTurn(this.stateView, net.sessionId);
  }

  private isMyTurn(): boolean {
    return !!this.stateView && this.stateView.currentTurn === net.sessionId && this.stateView.phase === 'aiming' && !net.isSpectator;
  }

  private myPlayer(): RoomPlayer | undefined {
    return this.stateView?.players.find((p) => p.sessionId === net.sessionId);
  }

  /**
   * Authentic input, per device:
   * - Mouse: the ball stays put and you aim by moving the cursor — a dashed
   *   guide line runs from ball to cursor and a solid line shows the actual
   *   stroke direction, its length capped at max power (the original 200px).
   *   Left-click strokes; any other mouse button cycles the shooting mode.
   * - Touch: drag anywhere to pull an aim line, release to stroke (the mouse
   *   scheme has no hover equivalent on touch).
   */
  private setupInput(): void {
    const pointerToGame = (event: PointerEvent): Phaser.Math.Vector2 => {
      const rect = this.game.canvas.getBoundingClientRect();
      const sx = CONFIG.GAME_WIDTH / rect.width;
      const sy = CONFIG.GAME_HEIGHT / rect.height;
      return new Phaser.Math.Vector2((event.clientX - rect.left) * sx, (event.clientY - rect.top) * sy);
    };

    const cycleMode = () => {
      this.mode = ((this.mode + 1) % 4) as ShootingMode;
      hud.setMode(MODE_NAMES[this.mode]);
      Sound.ui();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (this.isInteractivePointerTarget(event.target)) return;
      unlockAudio();

      if (event.pointerType === 'mouse') {
        // Original scheme: non-left buttons cycle the shooting mode.
        if (event.button !== 0) {
          if (this.isMyTurn()) cycleMode();
          return;
        }
        if (this.replay || !this.isMyTurn()) return;
        const me = this.myPlayer();
        if (!me || me.holed) return;
        this.mousePos = pointerToGame(event);
        this.shootAt(this.mousePos, me);
        return;
      }

      // Touch / pen: begin a drag-aim.
      if (event.button !== 0) return;
      if (this.replay || !this.isMyTurn()) return;
      const me = this.myPlayer();
      if (!me || me.holed) return;
      const pos = pointerToGame(event);
      this.aiming = true;
      this.activePointerId = event.pointerId;
      this.aimStart.set(toScreen(me.ballX), toScreen(me.ballY));
      this.dragStart.copy(pos);
      this.aimCurr.copy(this.aimStart);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType === 'mouse') {
        this.mousePos = pointerToGame(event);
        return;
      }
      if (!this.aiming || event.pointerId !== this.activePointerId) return;
      const pos = pointerToGame(event);
      this.aimCurr.set(this.aimStart.x + pos.x - this.dragStart.x, this.aimStart.y + pos.y - this.dragStart.y);
    };

    const release = (event: PointerEvent) => {
      if (event.pointerType === 'mouse') return;
      if (!this.aiming || event.pointerId !== this.activePointerId) return;
      this.aiming = false;
      this.activePointerId = null;
      this.aim.clear();
      const { dx, dy, dist } = this.touchAimNative();
      if (dist < POWER_DEAD_ZONE + 3) return; // too small; treat as cancel
      const me = this.myPlayer();
      if (me) this.tryShoot(dx, dy, me);
    };
    const cancel = (event: PointerEvent) => {
      if (!this.aiming || event.pointerId !== this.activePointerId) return;
      this.aiming = false;
      this.activePointerId = null;
      this.aim.clear();
    };

    // Right-click is a shooting-mode toggle, never a context menu, unless the
    // pointer is over interactive UI (chat, buttons).
    const onContextMenu = (event: MouseEvent) => {
      if (this.isInteractivePointerTarget(event.target)) return;
      event.preventDefault();
    };

    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('contextmenu', onContextMenu);
    this.unbinders.push(() => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('contextmenu', onContextMenu);
    });

    this.input.keyboard?.on('keydown-R', () => {
      const ae = document.activeElement;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA')) return; // typing in chat
      cycleMode();
    });
  }

  private isInteractivePointerTarget(target: EventTarget | null): boolean {
    return target instanceof HTMLElement && !!target.closest('button,input,select,textarea,.hud-overlay,.hud-chat');
  }

  /** Touch drag delta in native units. */
  private touchAimNative(): { dx: number; dy: number; dist: number } {
    const dx = (this.aimCurr.x - this.aimStart.x) / RENDER_SCALE;
    const dy = (this.aimCurr.y - this.aimStart.y) / RENDER_SCALE;
    return { dx, dy, dist: Math.hypot(dx, dy) };
  }

  /** Mouse cursor relative to my ball in native units, or null. */
  private mouseAimNative(me: RoomPlayer): { dx: number; dy: number; dist: number } | null {
    if (!this.mousePos) return null;
    const dx = this.mousePos.x / RENDER_SCALE - me.ballX;
    const dy = this.mousePos.y / RENDER_SCALE - me.ballY;
    return { dx, dy, dist: Math.hypot(dx, dy) };
  }

  private shootAt(cursor: Phaser.Math.Vector2, me: RoomPlayer): void {
    const dx = cursor.x / RENDER_SCALE - me.ballX;
    const dy = cursor.y / RENDER_SCALE - me.ballY;
    if (Math.hypot(dx, dy) < POWER_DEAD_ZONE + 3) return; // clicking the ball = no shot
    this.tryShoot(dx, dy, me);
  }

  /** Redraw the aim guide every frame (mouse hover-aim has no event to hook). */
  private drawAim(): void {
    this.aim.clear();
    if (this.replay || !this.isMyTurn()) return;
    const me = this.myPlayer();
    if (!me || me.holed) return;

    const vec = this.aiming ? this.touchAimNative() : this.mouseAimNative(me);
    if (!vec || vec.dist < 1) return;
    const { dx, dy, dist } = vec;

    const sx = toScreen(me.ballX);
    const sy = toScreen(me.ballY);

    const power = strokePower(dx, dy);
    const powerMag = Math.hypot(power.vx, power.vy);
    const t = Math.min(1, powerMag / MAX_POWER);
    const color = Phaser.Display.Color.Interpolate.ColorWithColor(
      new Phaser.Display.Color(120, 230, 140),
      new Phaser.Display.Color(255, 90, 90),
      100,
      Math.floor(t * 100),
    );
    const c = Phaser.Display.Color.GetColor(color.r, color.g, color.b);

    // Dashed guide: ball -> cursor (or drag point). The original drew this to
    // show the raw aim, distinct from the mode-transformed stroke direction.
    const cursX = this.aiming ? this.aimCurr.x : sx + dx * RENDER_SCALE;
    const cursY = this.aiming ? this.aimCurr.y : sy + dy * RENDER_SCALE;
    this.aim.lineStyle(2, 0xffffff, 0.35);
    this.drawDashedLine(sx, sy, cursX, cursY, 10, 8);

    // Solid stroke line: mode-rotated aim direction, capped at max-power
    // length (200px native). The ball launches exactly along this line — in
    // Direct mode it tracks the cursor.
    const aim = strokeAim(dx, dy, this.mode);
    const ang = Math.atan2(aim.vy, aim.vx);
    const len = t * AIM_LINE_MAX_PX * RENDER_SCALE;
    const ex = sx + Math.cos(ang) * len;
    const ey = sy + Math.sin(ang) * len;
    this.aim.lineStyle(4, c, 0.95);
    this.aim.lineBetween(sx, sy, ex, ey);
    this.aim.fillStyle(c, 1);
    this.aim.fillCircle(ex, ey, 5);
    void dist;
  }

  private drawDashedLine(x1: number, y1: number, x2: number, y2: number, dash: number, gap: number): void {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.hypot(dx, dy);
    if (dist < 1) return;
    const ux = dx / dist;
    const uy = dy / dist;
    let d = 0;
    while (d < dist) {
      const e = Math.min(d + dash, dist);
      this.aim.lineBetween(x1 + ux * d, y1 + uy * d, x1 + ux * e, y1 + uy * e);
      d = e + gap;
    }
  }

  private tryShoot(dx: number, dy: number, me: RoomPlayer): void {
    if (!this.isMyTurn() || !this.collisionMap || !this.hole) return;
    const dist = Math.hypot(dx, dy);

    const intent = { dx, dy, mode: this.mode };
    net.send('stroke', intent);
    Sound.hit(Math.min(MAX_POWER, (dist - POWER_DEAD_ZONE) / POWER_DIVISOR));

    // Client prediction with the same seed (and same seeded stroke noise) the
    // server uses, so the predicted replay matches the authoritative result.
    const seed = strokeSeed(this.hole.holeIndex, me.strokesThisHole, me.sessionId);
    const vel = strokeVelocity(dx, dy, this.mode, seed);
    const balls: SimBall[] = this.stateView!.players.filter((p) => !p.holed).map((p) => ({
      id: p.sessionId,
      x: p.ballX,
      y: p.ballY,
      vx: p.sessionId === me.sessionId ? vel.vx : 0,
      vy: p.sessionId === me.sessionId ? vel.vy : 0,
      holed: false,
      strokesThisHole: p.strokesThisHole,
      drowned: false,
    }));
    const result = simulateStroke({ balls, map: this.collisionMap, cup: this.cup, tee: this.hole.tee, collision: this.stateView!.collisionEnabled, seed });
    this.startReplay(result);
    this.predicting = true;
  }

  // ── Replay ──────────────────────────────────────────────────
  private onStrokeResult(p: { by: string; result: StrokeResult }): void {
    if (this.predicting && p.by === net.sessionId) {
      // already predicted locally
      this.predicting = false;
      return;
    }
    Sound.hit(3);
    this.startReplay(p.result);
  }

  private startReplay(result: StrokeResult): void {
    // Finalize any replay still in flight so a fast follow-up never cuts the
    // previous animation back to frame 0.
    if (this.replay) this.finishReplay();
    this.replay = { result, start: this.time.now, idx: 0, soundsDone: new Set() };
    // schedule event sounds on the same clock the replay samples use
    for (const ev of result.events) {
      const delay = ev.frame * FRAME_MS;
      this.time.delayedCall(delay, () => {
        if (ev.t === 'sink') Sound.sink();
        else if (ev.t === 'water') Sound.water();
        else if (ev.t === 'bounce') Sound.bounce();
        else if (ev.t === 'wall') Sound.wall();
        else if (ev.t === 'teleport') Sound.teleport();
        else if (ev.t === 'mine') Sound.mine();
      });
    }
    // Course changes (blown mines, cracked walls, sliding blocks) repaint at
    // the exact replay moment they happened in the simulation.
    for (const m of result.mutations ?? []) {
      this.pendingMutations.push({ at: this.replay.start + m.frame * FRAME_MS, m });
    }
  }

  /** Apply due course mutations to the local track and repaint the field. */
  private applyDueMutations(now: number, flushAll = false): void {
    if (this.pendingMutations.length === 0 || !this.track) return;
    const due = this.pendingMutations.filter((pm) => flushAll || pm.at <= now);
    if (due.length === 0) return;
    this.pendingMutations = this.pendingMutations.filter((pm) => !due.includes(pm));
    applyMutationsToTrack(this.track, due.map((pm) => pm.m));
    this.mutatedThisReplay = true;
    const canvas = paintField(this.track);
    if (this.textures.exists(this.fieldKey)) this.textures.remove(this.fieldKey);
    this.textures.addCanvas(this.fieldKey, canvas);
    this.fieldImage?.setTexture(this.fieldKey);
  }

  update(_time: number, _delta: number): void {
    this.drawAim();
    if (!this.replay) return;
    this.applyDueMutations(this.time.now);
    const { result } = this.replay;
    const samples = result.samples;
    if (samples.length === 0) {
      this.finishReplay();
      return;
    }
    const elapsed = this.time.now - this.replay.start;
    let acc = 0;
    let i = 0;
    while (i < samples.length - 1 && acc + samples[i + 1].dt <= elapsed) {
      acc += samples[i + 1].dt;
      i++;
    }
    if (i >= samples.length - 1) {
      // final
      const last = samples[samples.length - 1];
      result.ballIds.forEach((id, bi) => {
        const o = result.outcomes[bi];
        this.setBallPos(id, last.p[bi][0], last.p[bi][1], o?.holed ?? false);
      });
      this.finishReplay();
      return;
    }
    const a = samples[i];
    const b = samples[i + 1];
    const segT = b.dt > 0 ? (elapsed - acc) / b.dt : 1;
    result.ballIds.forEach((id, bi) => {
      const ax = a.p[bi][0];
      const ay = a.p[bi][1];
      const bx = b.p[bi][0];
      const by = b.p[bi][1];
      this.setBallPos(id, ax + (bx - ax) * segT, ay + (by - ay) * segT, false);
    });
  }

  private finishReplay(): void {
    const result = this.replay?.result;
    this.replay = null;
    this.predicting = false;
    // Flush any course changes still pending, then keep prediction in sync
    // with the server by rebuilding the collision map from the mutated track.
    this.applyDueMutations(this.time.now, true);
    if (this.mutatedThisReplay && this.track) {
      this.collisionMap = buildCollisionMap(this.track);
      this.mutatedThisReplay = false;
    }
    if (this.stateView) this.updateTeeOffsets(this.stateView);
    if (result) {
      // Reconcile to the replay's own deterministic end state, which matches the
      // server. Snapping to stateView here would warp the ball backwards whenever
      // the schema patch lags the stroke_result message.
      result.ballIds.forEach((id, bi) => {
        const o = result.outcomes[bi];
        if (!o) return;
        if (o.drowned && o.resetX !== undefined && o.resetY !== undefined) {
          // Drowned balls respawn (water: stroke start; acid: tee).
          this.setBallPos(id, o.resetX, o.resetY, o.holed);
        } else {
          this.setBallPos(id, o.x, o.y, o.holed);
        }
        if (o.holed) {
          const b = this.balls.get(id);
          if (b) this.popBall(b);
        }
      });
      // Balls not involved in this stroke stay where authoritative state says.
      if (this.stateView) {
        for (const p of this.stateView.players) {
          if (!result.ballIds.includes(p.sessionId)) this.setBallPos(p.sessionId, p.ballX, p.ballY, p.holed);
        }
      }
    } else if (this.stateView) {
      for (const p of this.stateView.players) this.setBallPos(p.sessionId, p.ballX, p.ballY, p.holed);
    }
    if (this.stateView) hud.updateTurn(this.stateView, net.sessionId);
    this.maybeShowHoleComplete();
  }

  /** Hole-complete arrives as a server message; hold the celebration until the
   * winning ball has actually finished its sink animation on screen. */
  private onHoleComplete(payload: unknown): void {
    this.pendingHoleComplete = payload;
    if (!this.replay) this.maybeShowHoleComplete();
  }

  private maybeShowHoleComplete(): void {
    if (!this.pendingHoleComplete || this.replay) return;
    const payload = this.pendingHoleComplete;
    this.pendingHoleComplete = null;
    // Let the sink "pop" tween (220ms) land before the card appears.
    this.time.delayedCall(260, () => hud.showHoleComplete(payload));
  }

  private popBall(b: BallGfx): void {
    this.tweens.add({ targets: [b.circle, b.shadow], scale: 0, duration: 220, ease: 'Back.in', onComplete: () => { b.circle.setScale(1); b.shadow.setScale(1); } });
  }

  private onEmote(p: { sessionId: string; emote: string }): void {
    const b = this.balls.get(p.sessionId);
    if (!b) return;
    const t = this.add.text(b.circle.x, b.circle.y - 30, p.emote, { fontSize: '28px' }).setOrigin(0.5).setDepth(50);
    this.tweens.add({ targets: t, y: t.y - 40, alpha: 0, duration: 1200, onComplete: () => t.destroy() });
  }

  private onGameOver(p: unknown): void {
    const data = p as { winnerId?: string };
    this.gameOverShown = true;
    Music.stop(); // let the win/lose fanfare have the stage
    if (data.winnerId === net.sessionId) Sound.win();
    else Sound.lose();
    hud.showGameOver(p, net.sessionId);
  }
}
